import { useEffect, useState, useRef } from "react";
import { flushSync } from "react-dom";
import { api, formatApiError } from "@/lib/api";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem, SelectSeparator } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * SelectWithAdd — a Select where users can add new options that persist to the backend.
 *
 * The `optionsKey` names the bucket (e.g. "status", "print_type"). Backend seeds sensible
 * defaults. Adding a value hits POST /api/options → other opened dialogs pick up the new
 * value the next time they mount.
 */
export function SelectWithAdd({
  optionsKey,
  value,
  onChange,
  placeholder = "—",
  testId,
  className,
}) {
  const [options, setOptions] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [newVal, setNewVal] = useState("");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  const load = () =>
    api.get("/options").then(({ data }) => {
      setOptions(data[optionsKey] || []);
    }).catch(() => setOptions([]));

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [optionsKey]);

  useEffect(() => {
    if (showAdd) setTimeout(() => inputRef.current?.focus(), 30);
  }, [showAdd]);

  const commit = async () => {
    const v = newVal.trim();
    if (!v) return;
    setSaving(true);
    try {
      const { data } = await api.post("/options", { key: optionsKey, value: v });
      // flushSync forces React to commit the new options list synchronously so
      // Radix's SelectItem map contains the new value BEFORE we call onChange —
      // otherwise the trigger cannot resolve the label and shows the placeholder.
      flushSync(() => setOptions(data.values || []));
      onChange?.(v);
      setNewVal("");
      setShowAdd(false);
      toast.success(`Added "${v}"`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  return (
    <div className={className}>
      <Select value={value || ""} onValueChange={(v) => {
        if (v === "__add_new__") { setShowAdd(true); return; }
        onChange?.(v);
      }}>
        <SelectTrigger data-testid={testId} className="rounded-sm">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent className="max-h-72">
          {/* A value already set (e.g. autofilled from an earlier job, or imported from Excel) must
              show even before anyone has ever picked it from this list — Radix's Select can only
              label a value that has a matching SelectItem, otherwise it silently shows the placeholder
              even though the real value is set underneath. */}
          {value && !options.includes(value) && <SelectItem key={value} value={value}>{value}</SelectItem>}
          {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
          {options.length > 0 && <SelectSeparator />}
          <SelectItem value="__add_new__" data-testid={`${testId}-add-item`}>
            <span className="flex items-center gap-1.5 text-[#FF5A00] font-medium">
              <Plus size={12} /> Add new…
            </span>
          </SelectItem>
        </SelectContent>
      </Select>

      {showAdd && (
        <div className="mt-1.5 flex items-center gap-1.5">
          <Input
            ref={inputRef}
            data-testid={`${testId}-add-input`}
            value={newVal}
            onChange={(e) => setNewVal(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); commit(); }
              if (e.key === "Escape") { setNewVal(""); setShowAdd(false); }
            }}
            placeholder="Type new value & press Enter"
            className="rounded-sm h-8 text-sm"
          />
          <Button
            type="button"
            size="sm"
            data-testid={`${testId}-add-save`}
            disabled={saving || !newVal.trim()}
            onClick={commit}
            className="h-8 rounded-sm bg-[#FF5A00] hover:bg-[#e04f00] px-2.5"
          >
            <Check size={13} />
          </Button>
        </div>
      )}
    </div>
  );
}
